import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { normalizeAddress } from './utils/normalizeAddress';

// Fix default marker icon paths (for CRA)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

function normalizeCoords(rawGeo) {
  if (!rawGeo || typeof rawGeo !== 'object') return null;
  const lat = Number(rawGeo.lat);
  const lng = Number(rawGeo.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function buildAddressLabel({ street, city, state, postcode, country }) {
  return [street, city, state, postcode, country].filter(Boolean).join(', ');
}

function normalizeAddressLabel(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function sameCoords(left, right) {
  return Array.isArray(left)
    && Array.isArray(right)
    && left.length === 2
    && right.length === 2
    && left[0] === right[0]
    && left[1] === right[1];
}

function RecenterMap({ coords }) {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(coords) || coords.length !== 2) return;
    map.setView(coords);
  }, [coords, map]);

  return null;
}

export default function TeamMap({
  address
}) {
  const normalizedAddress = normalizeAddress(address);
  const addressLabel = buildAddressLabel(normalizedAddress);
  const geoLat = normalizedAddress?.geo?.lat ?? null;
  const geoLng = normalizedAddress?.geo?.lng ?? null;
  const savedCoords = useMemo(() => normalizeCoords({
    lat: geoLat,
    lng: geoLng
  }), [geoLat, geoLng]);
  const savedGeoMatchesAddress = Boolean(
    savedCoords
    && normalizedAddress?.geoLabel
    && normalizeAddressLabel(normalizedAddress.geoLabel) === normalizeAddressLabel(addressLabel)
  );
  const [coords, setCoords] = useState(() => (savedGeoMatchesAddress ? savedCoords : null));
  const [status, setStatus] = useState(() => (savedGeoMatchesAddress ? 'ready' : 'loading'));
  const {
    street,
    city,
    state,
    postcode,
    country,
    geoLabel
  } = normalizedAddress;

  const encodedAddress = encodeURIComponent(addressLabel);
  const mapsHref = addressLabel ? `https://www.openstreetmap.org/search?query=${encodedAddress}` : '';

  useEffect(() => {
    const savedGeoMatchesAddress = Boolean(
      savedCoords
      && geoLabel
      && normalizeAddressLabel(geoLabel) === normalizeAddressLabel(addressLabel)
    );

    if (savedCoords && savedGeoMatchesAddress) {
      setCoords((current) => sameCoords(current, savedCoords) ? current : savedCoords);
      setStatus((current) => current === 'ready' ? current : 'ready');
      return undefined;
    }

    async function geocode() {
      const primary = [street, city, state, postcode, country].filter(Boolean);
      const fallback = [postcode, city, state, country].filter(Boolean);
      const parts = street ? primary : fallback;
      if (parts.length === 0) {
        setCoords(null);
        setStatus((current) => current === 'empty' ? current : 'empty');
        return;
      }

      setStatus((current) => current === 'loading' ? current : 'loading');
      const query = encodeURIComponent(parts.join(', '));
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${query}`;
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Geocoding failed with status ${res.status}`);
        const data = await res.json();
        if (data && data.length > 0) {
          const nextCoords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          setCoords((current) => sameCoords(current, nextCoords) ? current : nextCoords);
          setStatus((current) => current === 'ready' ? current : 'ready');
          return;
        }
        setCoords(null);
        setStatus((current) => current === 'error' ? current : 'error');
      } catch (e) {
        setCoords(null);
        setStatus((current) => current === 'error' ? current : 'error');
      }
    }
    geocode();
  }, [addressLabel, city, country, geoLabel, postcode, savedCoords, state, street]);

  if (status === 'loading' && !coords) return <p>Loading map…</p>;
  if (!coords) {
    return <div style={{
      display: 'grid',
      gap: '0.4rem'
    }}>
        <p className="text-muted" style={{
        margin: 0
      }}>
          {status === 'empty' ? 'No address details are available for this map yet.' : 'Map preview unavailable on this device/browser right now.'}
        </p>
        {mapsHref && <a href={mapsHref} target="_blank" rel="noreferrer" className="link">
            Open address in map search
          </a>}
      </div>;
  }

  return <MapContainer center={coords} zoom={13} style={{
    height: '100%',
    width: '100%'
  }}>
      <RecenterMap coords={coords} />
      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Marker position={coords}>
        <Popup>
          {addressLabel}
        </Popup>
      </Marker>
    </MapContainer>;
}
